import React, { FC, memo, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useButton, useI18n } from '~Hooks';
import Loader from '~ComponentsImproved/Loader';
import Icon from '~ComponentsImproved/Icon';
import redeemIcon from '~Assets/icons/redeem.svg';
import { POSPayment } from '~reducers';
import terminalImage from '../../payment.webp';
import baemingoLogo from '~Assets/Logos/baemingo_logo_black.svg';
import StepFrame from './StepFrame';

/**
 * Reserves the photo's exact box before it has loaded (padding-bottom is the
 * photo's 3297/4000 aspect ratio), so the step never jumps when it arrives.
 */
const TerminalFrame = styled.div`
  position: relative;
  width: min(60%, 420px);
  padding-bottom: calc(min(60%, 420px) * 3297 / 4000);
  margin: 8px 0;
  border-radius: 24px;
  overflow: hidden;
  background: var(--colorBlueGray100);
`;

const Terminal = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/** Gift card in place of the terminal photo: same box in the theme's primary, accent icon centered. */
const GiftCardArt = styled.div`
  position: absolute;
  inset: 0;
  background: var(--eposPrimary);
  display: flex;
  align-items: center;
  justify-content: center;
`;

/** "Powered by Baemingo", small and quiet at the very bottom, under the actions too. */
const PoweredBy = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  opacity: 0.45;
`;

const PoweredByLogo = styled.img`
  height: 14px;
`;

/** Spinner and status text on one line; eases into red on failure. */
const Status = styled.div`
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 600;
  opacity: 0.75;
  transition: color 220ms ease-out, opacity 220ms ease-out;

  &.is-failed {
    opacity: 1;
    color: var(--colorUIRed);
  }
`;

/**
 * The spinner folds shut sideways on failure so the text glides to center
 * instead of jumping. `visibility` flips after the fold, so the hidden
 * spinner stops painting.
 */
const SpinnerFold = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  opacity: 1;
  transition: grid-template-columns 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease-out,
    visibility 0s;

  .is-failed > & {
    grid-template-columns: 0fr;
    opacity: 0;
    visibility: hidden;
    transition: grid-template-columns 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease-out,
      visibility 0s 320ms;
  }
`;

const SpinnerInner = styled.div`
  min-width: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  padding-right: 16px;
`;

/** No word from the terminal for this long -> the shopper is offered a way out. */
export const STUCK_AFTER_MS = 3 * 60 * 1000;

/**
 * "Cancel" under the status line. Hidden until the payment has sat with the
 * same status for STUCK_AFTER_MS; shown by toggling `is-visible` on the node,
 * so the wait itself never renders anything.
 */
const AbortSlot = styled.div`
  margin-top: 8px;
  display: none;

  &.is-visible {
    display: block;
  }
`;

const ActionButton = styled.button`
  appearance: none;
  min-width: 280px;
  min-height: 88px;
  padding: 0 40px;
  border-radius: 24px;
  border: 2px solid var(--eposBorderColor);
  cursor: pointer;
  font-family: var(--eposFontFamily);
  font-size: 24px;
  font-weight: 700;
  background: transparent;
  color: var(--eposText);
  transition: transform 0.15s ease-out;

  &.is-primary {
    border-color: var(--eposButton);
    background: var(--eposButton);
    color: var(--eposButtonText);
  }

  &.is-touching {
    transform: scale(0.97);
  }
`;

interface Props {
  /** The terminal said no - show why and offer another go. */
  failed: boolean;
  paymentStatus: POSPayment['status'] | undefined;
  /** Retries with the receipt choice already on the order. */
  onRetry: (wantsReceipt: boolean) => void;
  onBack: () => void;
  /** The terminal has been silent for too long - abort the payment. */
  onAbort: () => void;
  /** A gift card is being charged - the gift card icon instead of the terminal photo. */
  giftCard: boolean;
}

/**
 * "Put in your card" while the terminal works, or a gift card loading state;
 * a retry and a way out if it fails.
 */
const PaymentStep: FC<Props> = ({ failed, paymentStatus, onRetry, onBack, onAbort, giftCard }) => {
  const { i18n } = useI18n();
  const abortSlotRef = useRef<HTMLDivElement>(null);

  // Every status change restarts the clock; the button only appears while still waiting.
  useEffect(() => {
    const slot = abortSlotRef.current;
    slot?.classList.remove('is-visible');
    if (failed) {
      return undefined;
    }
    const timer = window.setTimeout(() => slot?.classList.add('is-visible'), STUCK_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [failed, paymentStatus]);

  const { touching: abortTouching, disabled: ad, disabledToast: at, ...abortButton } = useButton({
    onClick: onAbort,
  });

  // The receipt choice is on the order already; retry keeps it by re-reading nothing.
  const handleRetry = useCallback(() => onRetry(true), [onRetry]);
  const { touching: retryTouching, disabled: rd, disabledToast: rt, ...retryButton } = useButton({
    onClick: handleRetry,
  });
  const { touching: backTouching, disabled: bd, disabledToast: bt, ...backButton } = useButton({
    onClick: onBack,
  });

  const statusKey = failed ? 'FAILED' : paymentStatus ?? 'PENDING';

  return (
    <StepFrame
      testId="epos-payment-step"
      title={i18n(giftCard ? 'Express.GiftCard.Paying' : 'SSK.Order.InsertYourCard')}
      subtitle={i18n(
        giftCard
          ? 'Express.GiftCard.PayingSubTitle'
          : 'SSK.Order.FollowTheInstructionsInThePaymentTerminal',
      )}
      actionsOpen={failed}
      actions={
        <>
          <ActionButton
            {...backButton}
            className={backTouching ? 'is-touching' : undefined}
            data-testid="epos-payment-back">
            {i18n('Express.Receipt.GoBack')}
          </ActionButton>
          <ActionButton
            {...retryButton}
            className={['is-primary', retryTouching && 'is-touching'].filter(Boolean).join(' ')}
            data-testid="epos-payment-retry">
            {i18n('General.Retry')}
          </ActionButton>
        </>
      }
      footer={
        <PoweredBy data-testid="epos-powered-by">
          {i18n('Menu.PoweredBy')}
          <PoweredByLogo src={baemingoLogo} alt="Baemingo" />
        </PoweredBy>
      }>
      <TerminalFrame data-testid="epos-payment-terminal">
        {giftCard ? (
          <GiftCardArt data-testid="epos-payment-gift-card">
            <Icon src={redeemIcon} color="var(--eposAccent)" width="160px" height="160px" />
          </GiftCardArt>
        ) : (
          <Terminal src={terminalImage} alt="" />
        )}
      </TerminalFrame>
      <Status className={failed ? 'is-failed' : undefined} data-testid="epos-payment-status">
        <SpinnerFold>
          <SpinnerInner>
            <Loader size="sm" color="var(--eposText)" />
          </SpinnerInner>
        </SpinnerFold>
        {i18n(`SSK.Checkout.Status.${statusKey}`)}
      </Status>
      {/* No abort while a gift card is charged - the charge may still land after it. */}
      {!giftCard && (
        <AbortSlot ref={abortSlotRef} data-testid="epos-payment-abort-slot">
          <ActionButton
            {...abortButton}
            className={abortTouching ? 'is-touching' : undefined}
            data-testid="epos-payment-abort">
            {i18n('General.Cancel')}
          </ActionButton>
        </AbortSlot>
      )}
    </StepFrame>
  );
};

export default memo(PaymentStep);
